/**
 * AuditExplorerPage — explorador dedicado de auditoria (A5 revamp · Brief 5 FE
 * Tela 6 · #310). Centro de auditoria do tenant: lista + filtros ricos +
 * timeline agregada + detalhe forense + cadeia por entidade (ADR-107).
 *
 * Gate (Opção A capability-driven): `audit-events-read` (Team+). O caminho
 * sensível (`audit-events-read-sensitive`, DPO-only) apenas desmascara payloads
 * — Timeline/cadeia ficam disponíveis com `audit-events-read` (alinhado ao
 * backend real, que exige só essa capability nos 4 endpoints). Ver inventário
 * de ações em `docs/superpowers/specs/2026-06-07-brief5-fe-t6-audit-inventario-acoes.md`.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';
import { FeatureLocked } from '../../shared/FeatureLocked';

import { periodToSince } from './audit-labels';
import { AuditDetailDrawer } from './AuditDetailDrawer';
import { AuditFilterBar, EMPTY_FILTERS } from './AuditFilterBar';
import { AuditLinearView } from './AuditLinearView';
import { AuditTimelineView } from './AuditTimelineView';
import { ConductorTurnsPanel } from './ConductorTurnsPanel';
import { useAuditEvents, useAuditTimeline } from './hooks/use-audit-events';

import type { AuditFilterState } from './AuditFilterBar';
import type { AuditEventWire, AuditListFilters, AuditTimelineFilters } from './types';

type ViewId = 'lista' | 'timeline';

const PAGE_LIMIT = 50;

function toListFilters(f: AuditFilterState): AuditListFilters {
  const since = periodToSince(f.period);
  return {
    limit: PAGE_LIMIT,
    ...(since !== undefined && { since }),
    ...(f.severity !== '' && { severity: f.severity }),
    ...(f.agent !== '' && { agent: f.agent }),
    ...(f.eventType.trim() !== '' && { eventType: f.eventType.trim() }),
    ...(f.category.trim() !== '' && { category: f.category.trim() }),
    ...(f.entityType.trim() !== '' && { entityType: f.entityType.trim() }),
    ...(f.entityId.trim() !== '' && { entityId: f.entityId.trim() }),
    ...(f.actor.trim() !== '' && { actor: f.actor.trim() }),
  };
}

// Item 335 — seed inicial dos filtros a partir dos search params da URL (salto do
// Centro de Alertas: `?entityType=conflict_alert&entityId=<id>`). Só leitura inicial
// (não sincroniza filtro↔URL nos dois sentidos — escopo de 1 fatia = 1 PR).
function seedFromParams(params: URLSearchParams): AuditFilterState {
  const entityType = params.get('entityType') ?? '';
  const entityId = params.get('entityId') ?? '';
  const eventType = params.get('eventType') ?? '';
  if (entityType === '' && entityId === '' && eventType === '') return EMPTY_FILTERS;
  // Salto por entidade/evento precisa achar o evento onde ele estiver — sem a janela default.
  return { ...EMPTY_FILTERS, period: 'all', entityType, entityId, eventType };
}

function toTimelineFilters(f: AuditFilterState, bucket: 'day' | 'hour'): AuditTimelineFilters {
  const { limit: _limit, ...rest } = toListFilters(f);
  return { ...rest, bucket };
}

export function AuditExplorerPage(): JSX.Element {
  const canRead = useCapability('audit-events-read');
  const canReadSensitive = useCapability('audit-events-read-sensitive');

  const [searchParams] = useSearchParams();
  const [view, setView] = useState<ViewId>('lista');
  // Lazy initializer — lê a URL uma única vez no mount (seed); mudanças posteriores
  // nos filtros vivem só no estado (não reescrevem a URL).
  const [filters, setFilters] = useState<AuditFilterState>(() => seedFromParams(searchParams));
  const [bucket, setBucket] = useState<'day' | 'hour'>('day');
  const [selected, setSelected] = useState<AuditEventWire | null>(null);
  const [exporting, setExporting] = useState<null | 'csv' | 'json'>(null);

  const listFilters = useMemo(() => toListFilters(filters), [filters]);
  const timelineFilters = useMemo(() => toTimelineFilters(filters, bucket), [filters, bucket]);

  const listQuery = useAuditEvents(listFilters, canRead && view === 'lista');
  const timelineQuery = useAuditTimeline(timelineFilters, canRead && view === 'timeline');

  const events = useMemo(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data],
  );

  // Export do conjunto FILTRADO (mesmos filtros da tela). Download autenticado via
  // Blob; o backend reaplica o mascaramento (sem sensitive → payloads mascarados).
  async function handleExport(format: 'csv' | 'json'): Promise<void> {
    setExporting(format);
    try {
      const blob = await api.exportAuditEvents(listFilters, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-events.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
    }
  }

  if (!canRead) {
    return <FeatureLocked capability="audit-events-read" />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between border-b border-border-subtle bg-surface px-8 py-5">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Explorador de auditoria</h1>
          <p className="mt-0.5 text-sm text-text-secondary">
            Eventos de auditoria do tenant — lista, timeline e cadeia por entidade.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              void (view === 'lista' ? listQuery.refetch() : timelineQuery.refetch());
            }}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-raised"
          >
            Atualizar
          </button>
          <button
            type="button"
            disabled={exporting !== null}
            onClick={() => void handleExport('csv')}
            title={
              canReadSensitive
                ? 'Exportar os eventos filtrados em CSV'
                : 'Exportar em CSV — detalhes sensíveis saem mascarados (acesso à auditoria sensível é restrito ao DPO)'
            }
            className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting === 'csv' ? 'Exportando…' : 'Exportar CSV'}
          </button>
          <button
            type="button"
            disabled={exporting !== null}
            onClick={() => void handleExport('json')}
            title={
              canReadSensitive
                ? 'Exportar os eventos filtrados em JSON'
                : 'Exportar em JSON — detalhes sensíveis saem mascarados (acesso à auditoria sensível é restrito ao DPO)'
            }
            className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting === 'json' ? 'Exportando…' : 'Exportar JSON'}
          </button>
        </div>
      </div>

      {!canReadSensitive && (
        <div className="border-b border-blue-100 bg-blue-50 px-8 py-2.5 text-xs text-blue-800">
          <strong>Acesso básico de auditoria.</strong> Detalhes de eventos sensíveis (severidade
          alta/forense) aparecem mascarados — a visualização integral é restrita a perfis com acesso
          à auditoria sensível (DPO).
        </div>
      )}

      <div className="flex gap-1 border-b border-border-subtle bg-surface px-8">
        {(['lista', 'timeline'] as const).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              view === v
                ? 'border-accent font-medium text-text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {v === 'lista' ? 'Lista' : 'Timeline'}
          </button>
        ))}
      </div>

      <AuditFilterBar filters={filters} onChange={setFilters} />

      {/* Item 481 — filtrado por UMA conversa, o rastro da decisão da IA aparece junto dos
          eventos: os turnos do conductor (ação + justificativa). Recusa em prosa não gera
          evento próprio; é aqui que ela fica legível para o DPO. */}
      {/^conversation$/i.test(filters.entityType.trim()) && filters.entityId.trim() !== '' && (
        <ConductorTurnsPanel conversationId={filters.entityId.trim()} />
      )}

      <div className="flex-1 overflow-auto">
        {view === 'lista' ? (
          <AuditLinearView
            events={events}
            isLoading={listQuery.isLoading}
            isError={listQuery.isError}
            hasMore={listQuery.hasNextPage}
            onLoadMore={() => void listQuery.fetchNextPage()}
            isLoadingMore={listQuery.isFetchingNextPage}
            onSelect={setSelected}
          />
        ) : (
          <AuditTimelineView
            data={timelineQuery.data}
            isLoading={timelineQuery.isLoading}
            isError={timelineQuery.isError}
            bucket={bucket}
            onBucketChange={setBucket}
          />
        )}
      </div>

      <AuditDetailDrawer
        event={selected}
        canReadSensitive={canReadSensitive}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
